import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { Crosshair, Maximize, Plus, RotateCw } from "lucide-react";
import {
  createGugikLayers,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
} from "@/components/map/gugik";
import { Spinner } from "@/components/ui/Spinner";
import { usePersistentToggle } from "@/hooks/usePersistentToggle";
import type { GeoJsonGeometry, Parcel } from "../../types";
import { parcelFillColor } from "../ui";
import {
  adjacentSheetPairsPuwg,
  centroidPuwg,
  formatFrameReadout,
  frameCornersPuwg,
  frameSizeMeters,
  pickCoveringScale,
} from "./frameGeometry";
import { latLngToPuwg, puwgToLatLng } from "./puwg";
import { useRawDxfWms } from "./useRawDxfWms";

// A frame in the editor: the canonical SheetFrame numbers plus a stable local
// uid (server frames carry no id — the page mints uids when loading them).
export interface EditableFrame {
  uid: string;
  order: number;
  center: [number, number]; // PUWG meters
  rotation_deg: number; // 0 (poziom/pion wg szablonu) lub 90
  scale_denominator: number;
  parcel_id: string | null;
  label: string;
}

// Optional attributes when adding a frame (orientation sheet sets scale/label).
export interface AddFrameOpts {
  scale_denominator?: number;
  rotation_deg?: number;
  label?: string;
}

// Geoman's per-layer controller, typed loosely (the lib's PMLayer type omits
// the drag mixin) — same pattern as ZEDrawingMap.tsx.
type PmLayer = L.Polygon & {
  pm?: {
    enableLayerDrag?: () => void;
    disableLayerDrag?: () => void;
  };
};

const BASE_STYLE: L.PathOptions = {
  color: "#dc1f1f",
  weight: 1.5,
  fillColor: "#dc1f1f",
  fillOpacity: 0.08,
};
const SELECTED_STYLE: L.PathOptions = {
  color: "#dc1f1f",
  weight: 3,
  fillColor: "#dc1f1f",
  fillOpacity: 0.15,
};

const numberIcon = (order: number, selected: boolean) =>
  L.divIcon({
    className: "",
    html: `<div style="pointer-events:none;display:flex;align-items:center;justify-content:center;width:22px;height:22px;background:${selected ? "#dc1f1f" : "#ffffff"};color:${selected ? "#ffffff" : "#dc1f1f"};border:1.5px solid #dc1f1f;font:700 11px/1 ui-monospace,monospace;">${order}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

// Podgląd numeracji sąsiednich arkuszy: badge „N ↔ M" na wspólnej krawędzi (to
// samo, co generator wypisze per arkusz na warstwie FORMATKA_SASIEDNIE).
const neighborIcon = (a: number, b: number) =>
  L.divIcon({
    className: "",
    html: `<div style="pointer-events:none;white-space:nowrap;display:flex;align-items:center;justify-content:center;height:18px;padding:0 7px;background:#1d4ed8;color:#fff;border:1px solid #fff;border-radius:9999px;font:700 10px/1 ui-monospace,monospace;box-shadow:0 1px 3px rgba(0,0,0,.5)">${a} ↔ ${b}</div>`,
    iconSize: [46, 18],
    iconAnchor: [23, 9],
  });

export function FrameMapEditor({
  srid,
  frames,
  selectedUid,
  viewportSizeMm,
  numberAdjacent,
  parcels,
  routeGeometry,
  gisProjectId,
  onSelect,
  onFrameCommit,
  onAddFrame,
  onRotateSelected,
}: {
  srid: number;
  frames: EditableFrame[];
  selectedUid: string | null;
  viewportSizeMm: number[] | undefined;
  /** Podgląd „N ↔ M" na stykających się krawędziach (zgodne z checkboxem zestawu). */
  numberAdjacent?: boolean;
  parcels: Parcel[] | undefined;
  routeGeometry: GeoJsonGeometry | null | undefined;
  gisProjectId: string | null | undefined;
  onSelect: (uid: string | null) => void;
  /** Geometry read back from the map after a drag — canonical PUWG (center only;
   *  drag never rotates). */
  onFrameCommit: (uid: string, patch: { center: [number, number] }) => void;
  /** „Dodaj arkusz” — center of the current map view, in PUWG meters. */
  onAddFrame: (centerPuwg: [number, number], opts?: AddFrameOpts) => void;
  /** „Obróć 90°” the selected frame (poziom ↔ pion). */
  onRotateSelected: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const contextGroupRef = useRef<L.LayerGroup | null>(null);
  const framesGroupRef = useRef<L.LayerGroup | null>(null);
  const neighborGroupRef = useRef<L.LayerGroup | null>(null);
  const layersRef = useRef(new Map<string, { poly: PmLayer; label: L.Marker }>());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onFrameCommitRef = useRef(onFrameCommit);
  onFrameCommitRef.current = onFrameCommit;

  const [zoom, setZoom] = useState(6);
  const [gugik, setGugik] = useGugikState("powerProjects.formatka", {
    parcels: false, infra: false, ortho: false, mpzp: false, gdos: false,
  });
  // Display preference — kept across remounts so unticking it isn't undone by leaving the editor.
  const [showRawDxf, setShowRawDxf] = usePersistentToggle(
    "powerProjects.formatka.showRawDxf",
    true,
  );
  const autoFittedRef = useRef(false);

  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);

  // ── map init (once) ────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    contextGroupRef.current = L.layerGroup().addTo(map);
    framesGroupRef.current = L.layerGroup().addTo(map);
    // Utworzona TU (nie leniwie), by po remount mapy (React StrictMode / cleanup
    // map.remove()) grupa siedziała na AKTUALNEJ mapie — inaczej badge sąsiadów
    // lądowałyby na zniszczonej mapie i były niewidoczne.
    neighborGroupRef.current = L.layerGroup().addTo(map);
    map.on("zoomend", () => setZoom(map.getZoom()));
    map.on("click", () => onSelectRef.current(null));
    mapRef.current = map;
    setZoom(map.getZoom());
    // The editor mounts inside a grid that lays out AFTER this effect, so the
    // map's container is 0-sized when created → any fitBounds would pick the
    // world view. Keep the map's size in sync via a ResizeObserver and let the
    // auto-fit effect fire once real dimensions exist.
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      layersRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef, gugikInfraRef, gugikOrthoRef, gugik, gugikMpzpRef, gugikGdosRef,
  );
  const rawDxf = useRawDxfWms(mapRef, gisProjectId, showRawDxf);

  // ── context layers: muted parcels + route, read-only (no popups here — they
  // would fight frame selection; that's why this is not MapTab) ──────────────
  useEffect(() => {
    const group = contextGroupRef.current;
    if (!group) return;
    group.clearLayers();
    for (const p of parcels ?? []) {
      if (!p.geometry || (!p.in_current_route && !p.is_manual)) continue;
      L.geoJSON(p.geometry as never, {
        interactive: false,
        style: {
          color: "#777",
          weight: 1,
          fillColor: parcelFillColor(p),
          fillOpacity: 0.15,
        },
      }).addTo(group);
    }
    if (routeGeometry) {
      L.geoJSON(routeGeometry as never, {
        interactive: false,
        style: { color: "#dc1f1f", weight: 2.5, opacity: 0.7 },
      }).addTo(group);
    }
  }, [parcels, routeGeometry]);

  // Bounds (latlng) of everything worth fitting to: frames first, else content.
  const contentBounds = (): L.LatLngBounds => {
    const b = L.latLngBounds([]);
    for (const f of frames) {
      const size = frameSizeMeters(viewportSizeMm, f.scale_denominator);
      for (const c of frameCornersPuwg(f, size)) {
        const ll = puwgToLatLng(srid, c);
        b.extend([ll.lat, ll.lng]);
      }
    }
    if (!b.isValid()) {
      const add = (g: unknown) => {
        if (!g) return;
        const gb = L.geoJSON(g as never).getBounds();
        if (gb.isValid()) b.extend(gb);
      };
      add(routeGeometry);
      for (const p of parcels ?? []) if (p.geometry) add(p.geometry);
    }
    return b;
  };

  const fitToContent = () => {
    const map = mapRef.current;
    if (!map) return;
    map.invalidateSize();
    const b = contentBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 20 });
  };
  const fitRef = useRef(fitToContent);
  fitRef.current = fitToContent;

  // One-time auto-fit once data exists — deferred past layout (double rAF +
  // invalidateSize inside fit) so the container has real pixels and the view
  // centers on the sheets instead of the whole country.
  useEffect(() => {
    if (autoFittedRef.current) return;
    const hasContent =
      frames.length > 0 || !!routeGeometry || (parcels ?? []).some((p) => p.geometry);
    if (!hasContent) return;
    autoFittedRef.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => fitRef.current()));
  }, [frames.length, routeGeometry, parcels]);

  // ── frames ↔ Leaflet sync (canonical → display; in-place updates keep
  // geoman handles alive) ────────────────────────────────────────────────────
  useEffect(() => {
    const group = framesGroupRef.current;
    if (!group) return;
    const seen = new Set<string>();
    for (const f of frames) {
      seen.add(f.uid);
      const size = frameSizeMeters(viewportSizeMm, f.scale_denominator);
      const latlngs = frameCornersPuwg(f, size).map((c) => {
        const ll = puwgToLatLng(srid, c);
        return [ll.lat, ll.lng] as L.LatLngTuple;
      });
      const centerLl = puwgToLatLng(srid, f.center);
      let entry = layersRef.current.get(f.uid);
      if (!entry) {
        const poly = L.polygon(latlngs, BASE_STYLE) as PmLayer;
        poly.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          onSelectRef.current(f.uid);
        });
        poly.addTo(group);
        const label = L.marker([centerLl.lat, centerLl.lng], {
          icon: numberIcon(f.order, false),
          interactive: false,
        }).addTo(group);
        entry = { poly, label };
        layersRef.current.set(f.uid, entry);
      } else {
        entry.poly.setLatLngs(latlngs);
        entry.label.setLatLng([centerLl.lat, centerLl.lng]);
      }
      const selected = f.uid === selectedUid;
      entry.poly.setStyle(selected ? SELECTED_STYLE : BASE_STYLE);
      entry.label.setIcon(numberIcon(f.order, selected));
      // Hover readout: rozmiar arkusza X/Y — papier (cm) : skala : teren (m).
      entry.poly.bindTooltip(
        `Arkusz ${f.order} · ${formatFrameReadout(f, viewportSizeMm)}`,
        { sticky: true, direction: "top", opacity: 0.95 },
      );
    }
    for (const [uid, entry] of [...layersRef.current.entries()]) {
      if (!seen.has(uid)) {
        entry.poly.remove();
        entry.label.remove();
        layersRef.current.delete(uid);
      }
    }
  }, [frames, selectedUid, srid, viewportSizeMm]);

  // ── podgląd numeracji sąsiednich arkuszy (badge „N ↔ M" na wspólnej krawędzi) ─
  useEffect(() => {
    const group = neighborGroupRef.current;
    if (!group) return;
    group.clearLayers();
    if (!numberAdjacent) return;
    for (const pair of adjacentSheetPairsPuwg(frames, viewportSizeMm)) {
      const ll = puwgToLatLng(srid, pair.anchor);
      L.marker([ll.lat, ll.lng], {
        icon: neighborIcon(pair.a, pair.b),
        interactive: false,
        keyboard: false,
      }).addTo(group);
    }
  }, [frames, viewportSizeMm, srid, numberAdjacent]);

  // ── drag (always on): move frames; on release read the centroid back in PUWG
  // and commit. Rotation is NOT free — it's toggled poziom/pion via buttons, so
  // dragging only ever changes the center. ──────────────────────────────────
  useEffect(() => {
    const cleanups: (() => void)[] = [];
    const commitCenter = (uid: string, poly: PmLayer) => {
      const ring = (poly.getLatLngs() as L.LatLng[][])[0] ?? [];
      if (ring.length < 4) return;
      const corners = ring
        .slice(0, 4)
        .map((ll) => latLngToPuwg(srid, { lat: ll.lat, lng: ll.lng }));
      onFrameCommitRef.current(uid, { center: centroidPuwg(corners) });
    };
    for (const [uid, { poly }] of layersRef.current.entries()) {
      try {
        poly.pm?.enableLayerDrag?.();
      } catch {
        /* layer may be mid-refresh */
      }
      const onDragEnd = () => commitCenter(uid, poly);
      poly.on("pm:dragend", onDragEnd);
      cleanups.push(() => {
        poly.off("pm:dragend", onDragEnd);
        try {
          poly.pm?.disableLayerDrag?.();
        } catch {
          /* noop */
        }
      });
    }
    return () => cleanups.forEach((fn) => fn());
  }, [frames, srid]);

  const addAtCenter = () => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    onAddFrame(latLngToPuwg(srid, { lat: c.lat, lng: c.lng }));
  };

  // Orientation ("mega") sheet: ONE frame that ENCOMPASSES the OTHER sheets —
  // their bounding box in PUWG, at the SMALLEST scale from the list that covers
  // it — so it shows the layout of the formatki. Falls back to route/parcels
  // only when there are no other sheets yet.
  const addOrientationSheet = () => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const others = frames.filter((f) => f.label !== "ORIENTACJA");
    for (const f of others) {
      const size = frameSizeMeters(viewportSizeMm, f.scale_denominator);
      for (const [x, y] of frameCornersPuwg(f, size)) {
        minX = Math.min(minX, x); maxX = Math.max(maxX, x);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      }
    }
    if (!Number.isFinite(minX)) {
      // No other sheets — fall back to the project footprint (route + parcels).
      const b = L.latLngBounds([]);
      const add = (g: unknown) => {
        if (!g) return;
        const gb = L.geoJSON(g as never).getBounds();
        if (gb.isValid()) b.extend(gb);
      };
      add(routeGeometry);
      for (const p of parcels ?? []) if (p.geometry) add(p.geometry);
      if (!b.isValid()) return;
      const sw = latLngToPuwg(srid, { lat: b.getSouth(), lng: b.getWest() });
      const ne = latLngToPuwg(srid, { lat: b.getNorth(), lng: b.getEast() });
      minX = Math.min(sw[0], ne[0]); maxX = Math.max(sw[0], ne[0]);
      minY = Math.min(sw[1], ne[1]); maxY = Math.max(sw[1], ne[1]);
    }
    const { scale, rotated } = pickCoveringScale(maxX - minX, maxY - minY, viewportSizeMm);
    onAddFrame([(minX + maxX) / 2, (minY + maxY) / 2], {
      scale_denominator: scale,
      rotation_deg: rotated ? 90 : 0,
      label: "ORIENTACJA",
    });
    // Zoom out so the new (larger) orientation frame is actually visible — it
    // now bounds the others, so a fit shows the whole layout. Deferred so the
    // frame is already in state/rendered.
    setTimeout(() => fitRef.current(), 120);
  };

  const toolBtn = (onClick: () => void, Icon: typeof Plus, label: string, title: string, disabled = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors disabled:opacity-40 disabled:hover:text-[var(--color-text-secondary)]"
      title={title}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      {/* Toolbar — od lewej krawędzi ODSUNIĘTY o szerokość leafletowego +/−:
          lewy górny róg (10–40 px) należy do kontrolki zoomu, a pasek z z-index 1000
          rysuje się NAD nią i zasłaniał przyciski przybliżania. */}
      <div className="absolute top-2 left-12 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm flex items-center divide-x divide-[var(--color-border)]">
        {toolBtn(onRotateSelected, RotateCw, "Obróć 90°", "Obróć zaznaczony arkusz (poziom ↔ pion)", !selectedUid)}
        {toolBtn(addAtCenter, Plus, "Dodaj arkusz", "Dodaj arkusz na środku widoku")}
        {toolBtn(addOrientationSheet, Maximize, "Orientacyjny", "Dodaj arkusz orientacyjny (cały projekt na jednej stronie)")}
      </div>

      {/* layers: GUGiK + raw DXF. `top-[78px]` = POD kontrolką zoomu (kończy się
          na ~70 px) — ta sama wysokość co w MapTab i ZEDrawingMap. */}
      <GugikLayerPanel
        gugik={gugik}
        onChange={setGugik}
        zoom={zoom}
        loading={gugikLoading}
        className="absolute top-[78px] left-2"
      />
      {rawDxf.available && (
        <label className="absolute top-2 right-2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2.5 py-1.5 font-mono text-[11px] text-[var(--color-text-secondary)] flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={showRawDxf}
            onChange={(e) => setShowRawDxf(e.target.checked)}
          />
          Surowy DXF
          {rawDxf.loading && <Spinner size="sm" className="w-3 h-3" />}
        </label>
      )}

      <button
        type="button"
        onClick={fitToContent}
        className="absolute bottom-2 left-2 z-[1000] card bg-[var(--color-bg-surface)] shadow-sm px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors inline-flex items-center gap-1.5"
      >
        <Crosshair size={13} /> Przybliż
      </button>
    </div>
  );
}
